/**
 * Fractions dans la prose d'un ÉNONCÉ (décision 2026-09-06).
 *
 * Une formule `$…$` qui n'est qu'une fraction seule est un MOT de la phrase
 * (« Karim mange 1/4 d'un gâteau ») : elle s'affiche en style texte (`\frac`),
 * petite et alignée sur la ligne. Une formule qui contient un calcul (« 3/4 +
 * 1/2 », « 3/4 × 8 », une égalité) est présentée : elle garde `\dfrac` et sa
 * ligne.
 *
 * Ne s'applique qu'aux énoncés (texte enrichi, HTML avec KaTeX, impression de
 * l'énoncé). Les étiquettes de QCM, relier, classer, ordonner, tableaux et
 * phrases à trous gardent la grande fraction : une fraction seule y est un
 * choix, pas un mot.
 */

// `$\dfrac{a}{b}$`, éventuellement précédé du `\mathrm{}` posé par tex() et
// d'un signe, rien d'autre : une fraction seule. Numérateur et dénominateur
// sans accolade imbriquée (des nombres, des lettres).
//
// La seconde forme, `{-\dfrac{a}{b}}`, est la fraction négative GROUPÉE
// (2026-09-23) : sans ses accolades, le `\mathrm{}` de tête fait du moins un
// opérateur binaire et KaTeX l'espace des deux côtés (« − 3/4 » au lieu de
// « −3/4 », vérifié). Sans cette alternative, une fraction négative seule
// restait en `\dfrac` là où la même fraction positive passait en `\frac` :
// dans une phrase, la négative s'affichait donc plus grande que la positive.
const FRACTION_SEULE = new RegExp(
    '^\\s*(?:\\\\mathrm\\{\\})?\\s*(?:'
    + '-?\\s*\\\\dfrac\\{[^{}]+\\}\\{[^{}]+\\}'          // \dfrac{a}{b}, -\dfrac{a}{b}
    + '|\\{\\s*-\\s*\\\\dfrac\\{[^{}]+\\}\\{[^{}]+\\}\\s*\\}'  // {-\dfrac{a}{b}}
    + ')\\s*$',
);

/**
 * Réécrit, dans un énoncé HTML, chaque formule réduite à une fraction seule
 * en style texte. Les autres formules et le reste du HTML sont intacts.
 * @param {string} html
 * @returns {string}
 */
export const fractionsEnProse = (html) => {
    if (typeof html !== 'string' || !html.includes('\\dfrac')) return html;
    return html.replace(/\$([^$]+)\$/g, (formule, contenu) =>
        (FRACTION_SEULE.test(contenu) ? `$${contenu.replace('\\dfrac', '\\frac')}$` : formule));
};
